/* eslint-disable react-hooks/exhaustive-deps */
import { useQueryClient } from "@tanstack/react-query";
import debounce from "lodash.debounce";

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  IconDecreaseVolume,
  IconIncreaseVolume,
  IconPause,
  IconPlay,
  IconSeekBack,
  IconSeekForward,
  IconVolume,
  IconVolumeOff,
} from "@/components/icons/player.icons";

import { FEED_KEYS, useUpdateEpisodeHistory } from "@/hooks/api/feed.hooks";
import { useLang } from "@/hooks/useLang";

import { usePlayerUIContext } from "../PlayerUIContext";
import { usePlayerControls2 } from "./PlayerControlsContext";

export const usePlayerControls = (videoRef: React.RefObject<HTMLVideoElement>) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const { showCenterContent } = usePlayerUIContext();
  const [tempTime, setTempTime] = useState(0);
  const { id, episodeId } = useParams() as { id: string; episodeId: string };
  const { setShowControls } = usePlayerControls2();

  const { mutate: mutateFn } = useUpdateEpisodeHistory();

  const queryClient = useQueryClient();
  const lang = useLang();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateHistory = useCallback(
    debounce((time: string) => {
      const formData = new FormData();
      formData.append("content_id", id.toString());
      formData.append("time_code", time);
      if (episodeId) {
        formData.append("episode_id", episodeId.toString());
      }
      mutateFn({ formData });
    }, 1000),
    [id, episodeId, mutateFn],
  );

  const updateIsPlaying = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      if (video.paused) {
        setShowControls(video?.paused);
      }
      setIsPlaying(!video.paused);
    }
  }, [videoRef]);

  const updateIsMuted = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setIsMuted(video.muted);
    }
  }, [videoRef]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      showCenterContent(<IconPause className="player-icon-center" />);
      video.play();
    } else {
      video.pause();
      showCenterContent(<IconPlay className="player-icon-center" />);
    }
    setTimeout(updateIsPlaying, 0);
  }, [videoRef, showCenterContent, updateIsPlaying]);

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const willBeMuted = !video.muted;
    video.muted = willBeMuted;

    if (willBeMuted) {
      video.volume = 0;
      setVolume(0);
    } else {
      video.volume = 1;
      setVolume(1);
    }

    showCenterContent(
      willBeMuted ? <IconVolumeOff className="player-icon-center" /> : <IconVolume className="player-icon-center" />,
    );
    setTimeout(updateIsMuted, 10);
  }, [videoRef, showCenterContent, updateIsMuted]);

  const setVolumeLevel = useCallback(
    (value: number) => {
      const video = videoRef.current;
      if (!video) return;
      if (volume > value) {
        showCenterContent(<IconDecreaseVolume className="player-icon-center" />);
      } else if (volume < value) {
        showCenterContent(<IconIncreaseVolume className="player-icon-center" />);
      } else return;
      setVolume(value);
      video.volume = value;
      if (video.muted && value > 0) {
        video.muted = false;
        updateIsMuted();
      }
    },
    [videoRef, volume, showCenterContent, updateIsMuted],
  );

  const seek = (time: number) => {
    const video = videoRef.current;
    if (!video) return;
    setShowControls(true);
    showCenterContent(
      time < 0 ? (
        <IconSeekBack className="player-icon-center !fill-transparent !stroke-white" />
      ) : (
        <IconSeekForward className="player-icon-center !fill-transparent !stroke-white" />
      ),
    );
    const newTime = Math.min(Math.max(video.currentTime + time, 0), video.duration);
    video.currentTime = newTime;
    updateHistory(Math.floor(newTime).toString());
    setTempTime(newTime);
  };

  const toggleFullScreen = () => {
    const container = document.getElementById("player-container");
    const video = videoRef.current;
    if (!container || !video) return;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-expect-error
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (isIOS) {
      // iOS: Use native video fullscreen
      const videoElement = video as HTMLVideoElement & {
        webkitEnterFullscreen?: () => void;
      };

      if (typeof videoElement.webkitEnterFullscreen === "function") {
        videoElement.webkitEnterFullscreen();
      } else {
        console.warn("iOS fullscreen API not supported.");
      }
    } else {
      // Standard Fullscreen API
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        container.requestFullscreen?.().catch((err) => {
          console.error("Fullscreen error:", err);
        });
      }
    }
  };

  // const toggleFullScreen = useCallback(() => {
  //   const playerContainer = document.getElementById("player-container");
  //   if (playerContainer) {
  //     if (document.fullscreenElement) {
  //       document.exitFullscreen();
  //     } else {
  //       playerContainer.requestFullscreen();
  //     }
  //   }
  // }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onVolumeChange = () => {
      setIsMuted(video.muted);
      setVolume(video.volume);
    };

    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    video.addEventListener("volumechange", onVolumeChange);

    return () => {
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("volumechange", onVolumeChange);
      queryClient.invalidateQueries({ queryKey: FEED_KEYS.contentDetailHistory(lang, +id) });
    };
  }, [videoRef]);

  return {
    isPlaying,
    isMuted,
    volume,
    togglePlay,
    toggleMute,
    setVolumeLevel,
    seek,
    toggleFullScreen,
    tempTime,
    updateHistory,
  };
};
